import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { getProjects } from '../lib/api';
import { badgeFacts } from '../lib/shellBadges';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import {
  HardHat,
  LayoutDashboard,
  LogOut,
  User as UserIcon,
  ChevronDown,
  Users,
  Building2,
  FolderCog,
  CalendarRange,
  AlertTriangle,
  Boxes,
  FlaskConical,
  ArrowLeftRight,
  ShieldCheck,
  ListChecks,
  FileText,
  CircleHelp,
  ClipboardCheck,
  Menu,
  Check,
  Settings,
} from 'lucide-react';
import { getRoleLabel } from '../lib/utils';
import NotificationBell from './NotificationBell';
import QuickActionsMenu from './QuickActionsMenu';
import ProjectSearch from './ProjectSearch';
import { useShellCrumb } from '../context/ShellCrumbContext';

// NAV-FE-1c — the record pages hand their path to the shell's breadcrumb.
// Pages import it from context/ShellCrumbContext, not from here: their tests
// stub this Layout module, and the setters must survive that.
export { useShellCrumb, setCrumb, clearCrumb } from '../context/ShellCrumbContext';

/* NAV-FE-1 — the v12 shell (docs/design/mockups/prototipo-shell-v12.html):
   a per-project sidebar in four groups, a project switcher in the top bar, a
   breadcrumb, and an avatar that keeps only Profile and Sign out. Every item
   leads to a page that already exists; the pages themselves do not change. */

/** The item a pathname belongs to. Order matters: the most specific first
 *  (an asset's ITP is an ITP, a project's handover is Handover, not Packages). */
const ACTIVE_RULES = [
  ['itps', (p) => p.includes('/itp-templates') || p.includes('/itp/')],
  ['tests', (p) => p.includes('/test-register') || p.includes('/test-requests/')],
  ['handover', (p) => p.includes('/handover')],
  ['ncrs', (p) => p.includes('/ncrs')],
  ['rfis', (p) => p.includes('/rfis')],
  ['qmp', (p) => p.includes('/quality-setup')],
  ['documents', (p) => p.includes('/documents')],
  ['settings', (p) => /^\/projects\/[^/]+\/settings\/?$/.test(p)],
  ['packages', (p) => /^\/projects\/[^/]+\/?$/.test(p) || p.includes('/qwps/') || p.includes('/quality-breakdown/')],
  ['home', (p) => p.startsWith('/dashboard')],
  ['assets', (p) => ['/asset-qa-table', '/assets/', '/lots/', '/drawings/', '/structured-itcs/'].some((x) => p.startsWith(x))],
  ['inspections', (p) => p.startsWith('/inspections') || p.startsWith('/inspection-register')],
  ['users', (p) => p.startsWith('/users')],
  ['companies', (p) => p.startsWith('/companies')],
];

export const activeNavKey = (pathname) => {
  const rule = ACTIVE_RULES.find(([, test]) => test(pathname));
  return rule ? rule[0] : null;
};

const NAV_LABELS = {
  home: 'Home',
  assets: 'Assets',
  inspections: 'Inspections',
  tests: 'Tests',
  handover: 'Handover',
  ncrs: 'NCRs',
  rfis: 'RFIs',
  qmp: 'QMP',
  itps: 'ITPs',
  packages: 'Packages & parties',
  documents: 'Documents',
  users: 'Users & companies',
  companies: 'Companies',
  settings: 'Project settings',
};

/** Pages that show ONE record of a project: switching project cannot keep them. */
const RECORD_PREFIXES = ['/assets/', '/lots/', '/drawings/', '/structured-itcs/', '/threads/', '/rfis/'];

/** Where switching to project `nextId` takes the user from `pathname`+`search`;
 *  null = stay where you are (the page is not project-scoped by its URL). */
export const projectSwitchTarget = (pathname, search, nextId) => {
  const home = `/dashboard?project_id=${nextId}`;
  // The project itself or one of its sections: the same page, in the other project.
  const section = pathname.match(/^\/projects\/[^/]+(\/[^/]+)?\/?$/);
  if (section) return `/projects/${nextId}${section[1] || ''}${search}`;
  // A record inside a project section (a QWP, a test request, a handover inspection).
  if (pathname.startsWith('/projects/')) return home;
  if (RECORD_PREFIXES.some((x) => pathname.startsWith(x))) return home;
  if (/^\/ncrs\/[^/]+/.test(pathname) && pathname !== '/ncrs/new') return home;
  if (pathname.startsWith('/dashboard')) return home;
  const params = new URLSearchParams(search);
  const key = ['project_id', 'project'].find((k) => params.has(k));
  if (key) {
    params.set(key, nextId);
    return `${pathname}?${params.toString()}`;
  }
  return null;
};

const initialsOf = (name) => (name || '')
  .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

// The project list outlives one page: every page mounts its own Layout, and a
// switcher that read "Select project" for a beat on every navigation would be
// noise. The list is still refetched on every mount.
let projectsCache = { key: null, list: [] };

// NAV-FE-1b — the shell's fixed heights: the top bar (h-14) and the breadcrumb
// row (h-9 = 16 px padding + one 20 px line).
const SHELL_TOP_PX = 56;
const CRUMB_ROW_PX = 36;

const ITEM_BASE = 'flex items-center gap-[10px] px-[10px] py-[7px] rounded-lg text-[13.5px]';

// NAV-FE-6 — the count pill's three tones (v12 `.b`, `.b.hot`, `.b.red`).
const BADGE_TONE = {
  neutral: 'bg-[#1e293b] text-[#cbd5e1]',
  hot: 'bg-[rgba(251,191,36,.15)] text-[#fbbf24]',
  red: 'bg-[rgba(248,113,113,.15)] text-[#f87171]',
};

const NavItem = ({ to, icon: Icon, label, active, disabled, title, badge, onNavigate, ...rest }) => {
  if (disabled) {
    return (
      <span
        role="link"
        aria-disabled="true"
        title="Select a project first"
        className={`${ITEM_BASE} font-medium text-[#64748b] cursor-not-allowed`}
        {...rest}
      >
        <Icon className="h-[18px] w-[18px] shrink-0 text-[#64748b]" />
        {label}
      </span>
    );
  }
  return (
    <Link
      to={to}
      title={title}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
      className={`${ITEM_BASE} ${active
        ? 'bg-[#172033] text-white font-semibold'
        : 'font-medium text-[#cbd5e1] hover:bg-[#0f172a] hover:text-white'}`}
      {...rest}
    >
      <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-[#f97316]' : 'text-[#8b95a7]'}`} />
      {label}
      {badge && (
        <span
          className={`ml-auto text-[11px] font-bold rounded-full px-[7px] py-[1px] ${BADGE_TONE[badge.tone] || BADGE_TONE.neutral}`}
          title={badge.title}
          data-testid="nav-badge"
          data-tone={badge.tone}
        >
          {badge.count}
        </span>
      )}
    </Link>
  );
};

const GroupLabel = ({ children }) => (
  <div className="text-[10px] font-bold uppercase tracking-[.1em] text-[#64748b] px-[10px] pt-[14px] pb-1 first:pt-1">
    {children}
  </div>
);

// QMP-FE-8b — `foldNav`: the page keeps its own rail (the QMP, mockup v83),
// so the project's navigation lives only in the ☰ sheet, at every width.
const Layout = ({ children, foldNav = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentProjectId, lastProjectId, setLastProjectId, projectsVersion, badges, refreshBadges,
  } = useProject();
  // The one project this shell is about (ProjectContext decides; lastProjectId
  // is the same value under its older name).
  const pid = currentProjectId ?? lastProjectId ?? null;
  // Tenant-wide check; not project-scoped.
  const { user, logout, isPlatformOwner } = useAuth();
  // LC9 — the ONE project-scoped question this shell asks. Read through the
  // hook rather than off `user.role`, because an existing internal user can be
  // granted `client_representative` on ONE project while keeping their own
  // tenant role: the UPA row is what decides, and the hook is what reads it.
  const { isClientRep, isAdmin } = useProjectPermissions(pid);

  const userKey = user?.id ?? user?.email ?? null;
  const [projects, setProjects] = useState(() => (projectsCache.key === userKey ? projectsCache.list : []));
  const [sheetOpen, setSheetOpen] = useState(false);

  // SHELL-REFRESH-1 — reread on `projectsVersion` too: Project settings bumps it
  // after a Save, so the switcher and the footer rename at once.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => getProjects())
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        projectsCache = { key: userKey, list };
        if (!cancelled) setProjects(list);
      })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, [userKey, projectsVersion]);

  const currentProject = pid ? projects.find((p) => p.id === pid) || null : null;
  const activeKey = activeNavKey(location.pathname);

  // NAV-FE-6 — the pills. On the Home the page feeds them with the read it
  // already makes; anywhere else the shell asks, and the context's one-minute
  // throttle makes ten pages in a minute cost one read.
  useEffect(() => {
    if (pid && activeKey !== 'home' && refreshBadges) refreshBadges(pid);
  }, [pid, activeKey, refreshBadges]);
  const facts = (key) => ((badges && badges.projectId === pid) ? badgeFacts(key, badges.counts[key]) : null);
  const { parts: crumbParts } = useShellCrumb();
  const showCrumb = !!(currentProject && (activeKey || crumbParts.length));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const switchProject = (nextId) => {
    if (setLastProjectId) setLastProjectId(nextId);
    const target = projectSwitchTarget(location.pathname, location.search, nextId);
    if (target) navigate(target);
  };

  /* LC9 — Setup (QMP, ITPs, Packages & parties) and the project-wide
     Inspection Register are the builder RUNNING the project: who is on it, how
     it is configured, what is booked next. The client is receiving the
     project, not running it, so none of them is offered to them. Documents
     stays (v12: the client reads the project's documents).

     Hiding is COURTESY, never the control. Every endpoint behind these screens
     refuses this role on the server (docs/design/asset-lifecycle.md §5.3.3);
     an entry that leads only to an error page is a worse answer than no
     entry. */
  // LC9 — `!isClientRep` in front of the existing test, not instead of it: an
  // internal admin who is ALSO the client representative on one project does
  // not carry the console into that project's context. C3 — any other member
  // reaches the same page as a read-only DIRECTORY of the current project, so
  // the item shows for them only while a project is in context.
  const showUsers = !isClientRep
    && (isPlatformOwner() || user?.role === 'company_qa_admin' || user?.role === 'admin' || !!pid);
  const showCompanies = isPlatformOwner();
  // NAV-FE-4b — Project settings: the project's admin, with a project in
  // context. The client representative never (LC9: courtesy; the server's
  // `admin` scope on PUT /project-settings is the control).
  const showSettings = !isClientRep && !!pid && !!isAdmin;
  const noProject = !pid;

  const renderNav = (onNavigate) => (
    <nav aria-label="Project" className="flex flex-col gap-[2px]">
      <GroupLabel>Work</GroupLabel>
      <NavItem
        to={pid ? `/dashboard?project_id=${pid}` : '/dashboard'}
        icon={LayoutDashboard} label="Home" active={activeKey === 'home'}
        onNavigate={onNavigate} data-testid="nav-dashboard"
      />
      <NavItem
        to="/asset-qa-table"
        icon={Boxes} label="Assets" active={activeKey === 'assets'} badge={facts('assets')}
        onNavigate={onNavigate} data-testid="nav-assets"
      />
      <NavItem
        to={`/inspections?project=${pid}`} disabled={noProject}
        icon={CalendarRange} label="Inspections" active={activeKey === 'inspections'} badge={facts('inspections')}
        onNavigate={onNavigate} data-testid="nav-inspections"
      />
      {!isClientRep && !noProject && (
        <Link
          to={`/inspection-register?project=${pid}`}
          onClick={onNavigate}
          className={`pl-[38px] pr-[10px] py-[3px] rounded-lg text-[12.5px] ${location.pathname.startsWith('/inspection-register')
            ? 'text-white font-semibold' : 'text-[#8b95a7] hover:text-white'}`}
          data-testid="nav-inspection-register"
        >
          All inspections
        </Link>
      )}
      <NavItem
        to={`/projects/${pid}/test-register`} disabled={noProject}
        icon={FlaskConical} label="Tests" active={activeKey === 'tests'}
        onNavigate={onNavigate} data-testid="nav-tests"
      />
      <NavItem
        to={`/projects/${pid}/handover`} disabled={noProject}
        icon={ArrowLeftRight} label="Handover" active={activeKey === 'handover'} badge={facts('handover')}
        onNavigate={onNavigate} data-testid="nav-handover"
      />

      <GroupLabel>Open items</GroupLabel>
      {/* NAV-FE-3b — the project's RFIs: every RFI the reader can already open
          (docs/design/rfi-list.md); the RFI itself opens at /rfis/:id. */}
      <NavItem
        to={`/projects/${pid}/rfis`} disabled={noProject}
        icon={CircleHelp} label="RFIs" title="Requests for information" active={activeKey === 'rfis'}
        badge={facts('rfis')} onNavigate={onNavigate} data-testid="nav-rfis"
      />
      {/* NCR-2 (D19 (8)) — the project's non-conformities; the list is fenced
          by the server (the NCRs of the packages the company works in). */}
      <NavItem
        to={`/projects/${pid}/ncrs`} disabled={noProject}
        icon={AlertTriangle} label="NCRs" title="Non-conformities" active={activeKey === 'ncrs'}
        badge={facts('ncrs')} onNavigate={onNavigate} data-testid="nav-ncrs"
      />
      {/* NAV-FE-3b — no page of its own: the Handover page's punch register.
          No active key either; on that page the active item stays Handover. */}
      <NavItem
        to={`/projects/${pid}/handover?section=punch`} disabled={noProject}
        icon={ClipboardCheck} label="Punch list" title="Punch list — on the Handover page"
        badge={facts('punch')} onNavigate={onNavigate} data-testid="nav-punch"
      />

      <GroupLabel>Setup</GroupLabel>
      {!isClientRep && (
        <>
          <NavItem
            to={`/projects/${pid}/quality-setup`} disabled={noProject}
            icon={ShieldCheck} label="QMP" active={activeKey === 'qmp'}
            onNavigate={onNavigate} data-testid="nav-qmp"
          />
          <NavItem
            to={`/projects/${pid}/itp-templates`} disabled={noProject}
            icon={ListChecks} label="ITPs" active={activeKey === 'itps'}
            onNavigate={onNavigate} data-testid="nav-itps"
          />
          <NavItem
            to={`/projects/${pid}`} disabled={noProject}
            icon={FolderCog} label="Packages & parties" active={activeKey === 'packages'}
            onNavigate={onNavigate} data-testid="nav-project-setup"
          />
        </>
      )}
      <NavItem
        to={`/projects/${pid}/documents`} disabled={noProject}
        icon={FileText} label="Documents" active={activeKey === 'documents'}
        onNavigate={onNavigate} data-testid="nav-documents"
      />

      {(showUsers || showCompanies || showSettings) && <GroupLabel>Admin</GroupLabel>}
      {showUsers && (
        <NavItem
          to={user?.role !== 'platform_owner' && pid ? `/users?project_id=${pid}` : '/users'}
          icon={Users} label="Users & companies" active={activeKey === 'users'}
          onNavigate={onNavigate} data-testid="nav-user-management"
        />
      )}
      {showSettings && (
        <NavItem
          to={`/projects/${pid}/settings`} disabled={noProject}
          icon={Settings} label="Project settings" active={activeKey === 'settings'}
          onNavigate={onNavigate} data-testid="nav-project-settings"
        />
      )}
      {showCompanies && (
        <NavItem
          to="/companies"
          icon={Building2} label="Companies" active={activeKey === 'companies'}
          onNavigate={onNavigate} data-testid="nav-companies"
        />
      )}
    </nav>
  );

  const footer = currentProject && (
    <div
      className="mt-auto pt-[10px] px-[10px] text-[11.5px] text-[#64748b] border-t border-[#1e293b] truncate"
      data-testid="side-foot"
    >
      {currentProject.project_code ? `${currentProject.name} · ${currentProject.project_code}` : currentProject.name}
    </div>
  );

  return (
    // NAV-FE-1b — `--shell-top`: how much of the viewport the shell takes above
    // a page (the 56 px top bar, plus the 36 px breadcrumb row when it shows),
    // for the pages that size themselves to the viewport.
    <div
      className="min-h-screen bg-slate-950 flex flex-col"
      style={{ '--shell-top': showCrumb ? `${SHELL_TOP_PX + CRUMB_ROW_PX}px` : `${SHELL_TOP_PX}px` }}
      data-testid="shell-root"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-50 h-14 flex items-center gap-[14px] px-4 bg-[rgba(15,23,42,.95)] backdrop-blur-lg border-b border-[#1e293b]">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={`${foldNav ? '' : 'lg:hidden '}text-[#cbd5e1] hover:text-white`}
          aria-label="Open navigation"
          data-testid="nav-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </button>
        {/* Quick actions drawer (left) — separate from the avatar menu on the
            right. Project comes from ProjectContext. */}
        <QuickActionsMenu projectId={pid} />
        <button
          type="button"
          onClick={() => navigate(pid ? `/dashboard?project_id=${pid}` : '/dashboard')}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          data-testid="header-logo"
        >
          <div className="h-[30px] w-[30px] rounded-lg bg-orange-500 flex items-center justify-center">
            <HardHat className="h-4 w-4 text-white" />
          </div>
          <span className="text-[17px] font-extrabold text-white whitespace-nowrap hidden sm:block">ConstructQA</span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 min-w-0 max-w-[260px] bg-[#0b1222] border border-[#334155] rounded-lg px-[10px] py-[5px] text-white font-semibold text-[13px]"
              data-testid="project-switcher-top"
            >
              <span className="truncate">{currentProject ? currentProject.name : 'Select project'}</span>
              {currentProject?.project_code && (
                <span className="text-[#8b95a7] font-medium text-[11.5px] shrink-0">{currentProject.project_code}</span>
              )}
              <ChevronDown className="h-[11.5px] w-[11.5px] text-[#8b95a7] shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-72 max-h-[60vh] overflow-y-auto bg-slate-900 border-slate-800" align="start">
            {projects.length === 0 && (
              <div className="px-3 py-2 text-[13px] text-[#8b95a7]">No projects</div>
            )}
            {projects.map((p) => (
              <DropdownMenuItem
                key={p.id}
                onSelect={() => { if (p.id !== pid) switchProject(p.id); }}
                className="text-slate-300 focus:bg-slate-800 focus:text-white cursor-pointer gap-2"
                data-testid={`project-option-${p.id}`}
                data-current={p.id === pid ? 'true' : undefined}
              >
                <Check className={`h-4 w-4 shrink-0 ${p.id === pid ? 'text-[#f97316]' : 'invisible'}`} />
                <span className="truncate">{p.name}</span>
                {p.project_code && <span className="ml-auto text-[11.5px] text-[#8b95a7] shrink-0">{p.project_code}</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <ProjectSearch projectId={pid} />

        <div className="ml-auto flex items-center gap-[14px] text-[#8b95a7]">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-auto flex items-center gap-2 px-1.5 py-1 hover:bg-slate-800"
                data-testid="user-menu-btn"
              >
                <div className="h-[30px] w-[30px] rounded-full bg-[#1e293b] flex items-center justify-center text-[12px] font-bold text-[#cbd5e1]">
                  {initialsOf(user?.name) || <UserIcon className="h-4 w-4" />}
                </div>
                <div className="hidden md:block text-left leading-[1.1]">
                  <p className="text-[12.5px] text-white">{user?.name}</p>
                  <p className="text-[10.5px] text-[#8b95a7]">{getRoleLabel(user?.role)}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-[#64748b]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-slate-900 border-slate-800" align="end">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-xs text-orange-500 mt-1">{getRoleLabel(user?.role)}</p>
              </div>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem
                onClick={() => navigate('/profile')}
                className="text-slate-300 focus:bg-slate-800 focus:text-white cursor-pointer"
                data-testid="nav-profile"
              >
                <UserIcon className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex flex-1">
        <aside
          className={`hidden ${foldNav ? '' : 'lg:flex '}w-[232px] flex-none flex-col bg-[#070d1f] border-r border-[#1e293b] pt-3 px-[10px] pb-5 sticky top-14 h-[calc(100vh-56px)] overflow-y-auto`}
          data-testid="side-nav"
        >
          {renderNav(undefined)}
          {footer}
        </aside>

        {/* Below lg the bar folds into a sheet from the left — the SAME items. */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent
            side="left"
            className="w-[260px] sm:max-w-[260px] bg-[#070d1f] border-[#1e293b] p-0 text-[#cbd5e1]"
            data-testid="side-nav-sheet"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">The project&apos;s pages</SheetDescription>
            <div className="flex flex-col h-full pt-12 px-[10px] pb-5 overflow-y-auto">
              {renderNav(() => setSheetOpen(false))}
              {footer}
            </div>
          </SheetContent>
        </Sheet>

        <main className="flex-1 min-w-0">
          {showCrumb && (
            <div className="max-w-[1360px] mx-auto px-4 md:px-6 pt-4 h-9 overflow-hidden">
              <Breadcrumb data-testid="crumb">
                <BreadcrumbList className="gap-1.5 sm:gap-1.5 flex-nowrap text-[12.5px] leading-5 text-[#8b95a7]">
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild className="hover:text-white">
                      <Link to={`/dashboard?project_id=${pid}`} data-testid="crumb-project">{currentProject.name}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {crumbParts.length ? crumbParts.map((part, i) => (
                    // NAV-FE-1c — the page's own path: each part with `to` is a
                    // link (the way back), the one without is where you are.
                    <React.Fragment key={`${i}-${part.label}`}>
                      <BreadcrumbSeparator>›</BreadcrumbSeparator>
                      <BreadcrumbItem>
                        {part.to ? (
                          <BreadcrumbLink asChild className="text-orange-500 hover:text-orange-400">
                            <Link to={part.to} data-testid={`crumb-${i + 1}`}>{part.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <b
                            className="text-slate-50 font-semibold"
                            data-testid={i === crumbParts.length - 1 ? 'crumb-last' : `crumb-${i + 1}`}
                          >
                            {part.label}
                          </b>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  )) : (
                    <>
                      <BreadcrumbSeparator>›</BreadcrumbSeparator>
                      <BreadcrumbItem>
                        <BreadcrumbPage className="text-white font-semibold" data-testid="crumb-current">
                          {NAV_LABELS[activeKey]}
                        </BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
